import type { ContractType, UnitType } from '../types/agreement'

export interface UnitTypeOption {
  value: UnitType | string
  label: string
}

/** All unit types available in line-item dropdowns. */
export const ALL_UNIT_TYPE_OPTIONS: UnitTypeOption[] = [
  { value: 'flat_rate', label: 'Flat Rate' },
  { value: 'per_sqft', label: 'Per Sq Ft' },
  { value: 'per_hour', label: 'Per Hour' },
  { value: 'per_visit', label: 'Per Visit' },
  { value: 'per_month', label: 'Per Month' },
  { value: 'per_unit', label: 'Per Unit' },
  { value: 'per_apartment', label: 'Per Apartment' },
  { value: 'per_bedroom', label: 'Per Bedroom' },
  { value: 'per_bathroom', label: 'Per Bathroom' },
]

/** Unit types typically used on rate-card / usage-based contracts. */
export const RATE_CARD_UNIT_TYPES = new Set([
  'per_apartment',
  'per_bedroom',
  'per_bathroom',
  'per_hour',
  'per_unit',
  'per_visit',
  'per_sqft',
])

/** Map service-catalog unit_type strings to canonical line-item slugs. */
export function normalizeUnitType(catalogUnitType: string): string {
  const normalized = catalogUnitType.toLowerCase().trim()
  const unitTypeMap: Record<string, string> = {
    monthly: 'per_month',
    'per month': 'per_month',
    month: 'per_month',
    hourly: 'per_hour',
    'per hour': 'per_hour',
    hour: 'per_hour',
    sqft: 'per_sqft',
    'sq ft': 'per_sqft',
    'square foot': 'per_sqft',
    'square feet': 'per_sqft',
    visit: 'per_visit',
    'per visit': 'per_visit',
    unit: 'per_unit',
    'per unit': 'per_unit',
    apartment: 'per_apartment',
    'per apartment': 'per_apartment',
    bedroom: 'per_bedroom',
    'per bedroom': 'per_bedroom',
    bathroom: 'per_bathroom',
    'per bathroom': 'per_bathroom',
    flat: 'flat_rate',
    'flat rate': 'flat_rate',
  }
  return unitTypeMap[normalized] || catalogUnitType
}

export function formatUnitTypeLabel(unitType: string | null | undefined): string {
  const match = ALL_UNIT_TYPE_OPTIONS.find((o) => o.value === unitType)
  if (match) return match.label
  return (unitType || '').replace(/_/g, ' ')
}

/**
 * Filter unit-type dropdown options based on contract_type.
 * Rate-card unit types are included for one-time contracts (turnovers, hourly).
 */
export function getUnitTypeOptions(
  contractType: ContractType,
  options?: { includeRateCardTypes?: boolean }
): UnitTypeOption[] {
  const includeRateCard = options?.includeRateCardTypes ?? true
  if (contractType === 'one_time' || contractType === 'as_needed') {
    const base: UnitTypeOption[] = [
      { value: 'flat_rate', label: 'Flat Rate' },
      { value: 'per_sqft', label: 'Per Sq Ft' },
      { value: 'per_hour', label: 'Per Hour' },
      { value: 'per_unit', label: 'Per Unit' },
    ]
    if (includeRateCard) {
      base.push(
        { value: 'per_apartment', label: 'Per Apartment' },
        { value: 'per_bedroom', label: 'Per Bedroom' },
        { value: 'per_bathroom', label: 'Per Bathroom' }
      )
    }
    return base
  }
  return ALL_UNIT_TYPE_OPTIONS.filter((o) => o.value !== 'per_apartment' && o.value !== 'per_bedroom' && o.value !== 'per_bathroom')
}

/** Unit types that should usually be marked variable when used on line items. */
export function isUsageBasedUnitType(unitType: string | null | undefined): boolean {
  return RATE_CARD_UNIT_TYPES.has((unitType || '').toLowerCase())
}
